import React, { useEffect, createContext, useRef} from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';


export const WebSocketContext = createContext();

const WebSocketProvider = ({children}) => {
    const channels = useRef({})
    // FEAT-054 Phase 4c: incidents whose chat is currently open in this tab. Tracked so we can
    // re-send `subscribe` on every (re)connect — the backend forgets subscriptions on $disconnect.
    const openIncidents = useRef(new Set())
    const subscribe = (channel, callback) => {
        channels.current[channel] = callback
    }
    const unsubscribe = (channel) => {
        delete channels.current[channel]
    }
    const accessToken = localStorage.getItem('accessToken');
    const socketUrl = process.env.REACT_APP_wssAuthUrl;
    const webSocketOptions = {
        queryParams: {"x-api-key": accessToken },
        onOpen: () => {
          
        },
        onClose: () => {
        },
        onMessage(event) {
        },
        onError(event) {
        
        },
        retryOnError: false,
        reconnectAttempts: 1,
        shouldReconnect: () => true,
    }
    const { lastMessage, readyState, sendJsonMessage } = useWebSocket(socketUrl, webSocketOptions);

    // FEAT-054 Phase 4c: subscribe/unsubscribe this connection to an incident's chat fan-out so
    // it gets live messages even when the viewer isn't in the incident's assignedUsers (e.g. a
    // super-admin). Replaces the drawer's periodic poll as the fast path.
    const subscribeIncident = (incidentId) => {
        if (!incidentId) return;
        openIncidents.current.add(incidentId);
        try {
            sendJsonMessage({ action: "subscribe", incidentId, token: localStorage.getItem('accessToken') });
        } catch (e) { /* not open yet — the readyState effect re-sends on connect */ }
    }
    const unsubscribeIncident = (incidentId) => {
        if (!incidentId) return;
        openIncidents.current.delete(incidentId);
        try {
            sendJsonMessage({ action: "unsubscribe", incidentId });
        } catch (e) { /* ignore */ }
    }

    useEffect(() => {
        if (readyState === ReadyState.OPEN) {
            console.log('WebSocket is open');
        } else if (readyState === ReadyState.CLOSING) {
            console.log('WebSocket is closing');
        } else if (readyState === ReadyState.CLOSED) {
            console.log('WebSocket is closed');
        } else if (readyState === ReadyState.CONNECTING) {
            console.log('WebSocket is connecting');
        } else if (readyState === ReadyState.UNINSTANTIATED) {
            console.log('WebSocket is uninstantiated');
        } else {
            console.log('WebSocket is not connected');
        }
        // Phase 4c: on (re)connect, re-subscribe every incident whose chat is open in this tab.
        if (readyState === ReadyState.OPEN && openIncidents.current.size > 0) {
            const token = localStorage.getItem('accessToken');
            openIncidents.current.forEach((incidentId) => {
                try { sendJsonMessage({ action: "subscribe", incidentId, token }); } catch (e) { /* ignore */ }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readyState]);

    useEffect(() => {
        if (lastMessage) {
            // Tolerant parse: broadcast frames are double-encoded (json.dumps of an already-JSON
            // string), but API Gateway control/error frames (e.g. {"message":"Forbidden"}) and
            // keepalives are single-encoded or non-JSON. The old fixed double-parse threw
            // ("[object Object]" is not valid JSON) on any single-encoded frame and white-screened
            // the portal. Parse once, parse again only if still a string, and ignore anything
            // that isn't a well-formed command frame.
            let messageData;
            try {
                let parsed = typeof lastMessage.data === "string"
                    ? JSON.parse(lastMessage.data)
                    : lastMessage.data;
                if (typeof parsed === "string") {
                    parsed = JSON.parse(parsed);
                }
                messageData = parsed;
            } catch (e) {
                console.warn("WS frame is not valid JSON, ignoring:", lastMessage.data);
                return;
            }
            if (!messageData || typeof messageData.command !== "string") {
                // Control/error/keepalive frame with no command — nothing to route.
                return;
            }
            console.log("messageData", messageData)
            const command = messageData.command
            let channel = "incidents"
            if (command.includes("emergency")) {
                channel = "Emergency"
            }
            if (command.includes("Comment")) {
                channel = "comments"
            }
            // FEAT-054: the unified chat broadcasts command "message" — route it to the
            // "comments" channel where the Chat with Student drawer subscribes.
            if (command === "message") {
                channel = "comments"
            }
            if (channels.current[channel]){
                channels.current[channel](messageData)
            }
        }
    }
    , [lastMessage]);

    return (
        <WebSocketContext.Provider value={[subscribe, unsubscribe, subscribeIncident, unsubscribeIncident]}>
            {children}
        </WebSocketContext.Provider>
    )
}
export default WebSocketProvider;